import React, { useState, useEffect } from 'react';
import { repasseService, apoliceService } from '../../services';
import { formatDate, formatCurrency } from '../../utils/formatters';
import { ApoliceFormModal } from '../apolice/ApoliceFormModal';
import type { ApoliceData } from '../../services/apoliceService';
import { ConfirmDialog } from '../ui/ConfirmDialog';
import { useNotifier } from '../ui/Notifier';

interface RepasseTableProps {
  onEdit?: (repasse: any) => void;
  onView?: (repasse: any) => void;
  refreshKey?: number;
  onDeleted?: () => void;
}

export const RepasseTable: React.FC<RepasseTableProps> = ({
  onEdit,
  onView,
  refreshKey,
  onDeleted
}) => {
  const [repasses, setRepasses] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortField, setSortField] = useState<string>('dataPagamento');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 10;
  const [showApoliceModal, setShowApoliceModal] = useState(false);
  const [selectedApolice, setSelectedApolice] = useState<ApoliceData | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTargetItem, setDeleteTargetItem] = useState<any | null>(null);
  const notifier = useNotifier();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1); // Reset para primeira página ao buscar
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const loadRepasses = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Debug: verificar autenticação
      const token = localStorage.getItem('authToken');
      const user = localStorage.getItem('user');
      console.log('Token presente:', !!token);
      console.log('User presente:', !!user);
      console.log('Carregando repasses...', { currentPage, itemsPerPage, searchTerm: debouncedSearchTerm, sortField, sortOrder });
      
      const response = await repasseService.list({
        page: currentPage,
        limit: itemsPerPage,
        search: debouncedSearchTerm,
        sortBy: sortField,
        sortOrder
      });
      
      console.log('Resposta de repasses:', response);
      const data = response?.data || response || [];
      
      // Verifica se a resposta tem paginação do backend
      if (response?.pagination) {
        setRepasses(data);
        setTotalPages(response.pagination.totalPages || 1);
        console.log('Paginação do backend:', response.pagination);
      } else if (Array.isArray(data)) {
        setRepasses(data);
        setTotalPages(Math.ceil(data.length / itemsPerPage));
      } else if (data.items && Array.isArray(data.items)) {
        setRepasses(data.items);
        setTotalPages(data.totalPages || 1);
      } else {
        setRepasses([]);
        setTotalPages(1);
      }
    } catch (err: any) {
      console.error('Erro ao carregar repasses:', err);
      console.error('Status do erro:', err.status);
      console.error('Mensagem do erro:', err.message);
      setError(err.message || 'Erro ao carregar repasses');
      setRepasses([]);
      
      // Se for erro 401, não mostrar erro na tela pois o usuário já foi redirecionado
      if (err.status === 401) {
        return;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRepasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, debouncedSearchTerm, sortField, sortOrder, refreshKey]);

  const handleSortChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSortField(e.target.value);
    setCurrentPage(1);
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    setCurrentPage(1);
  };

  const handleDelete = (repasse: any) => {
    if (!repasse.id) return;
    setDeleteTargetItem(repasse);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTargetItem?.id) return;
    setConfirmOpen(false);
    try {
      await repasseService.remove(deleteTargetItem.id);
      notifier.show({ message: 'Repasse excluído com sucesso', severity: 'success' });
      loadRepasses();
      onDeleted?.();
    } catch (err: any) {
      notifier.show({ message: err.message || 'Erro ao excluir repasse', severity: 'error' });
    } finally {
      setDeleteTargetItem(null);
    }
  };

  const handleOpenApolice = async (repasse: any) => {
    if (repasse.documentoId) {
      try {
        console.log('Buscando apólice com ID:', repasse.documentoId);
        const apolice = await apoliceService.getById(repasse.documentoId);
        console.log('Apólice retornada:', apolice);
        setSelectedApolice((apolice && (apolice as any).data) ? (apolice as any).data : (apolice as any));
        setShowApoliceModal(true);
      } catch (err: any) {
        console.error('Erro ao carregar apólice:', err);
        notifier.show({ message: err.message || 'Erro ao carregar apólice', severity: 'error' });
      }
    }
  };

  const handleCloseApoliceModal = () => {
    setShowApoliceModal(false);
    setSelectedApolice(null);
  };

  // Não filtrar localmente - a busca é feita no backend
  // Usar repasses diretamente

  return (
    <div className="space-y-4">
      {/* Header com busca e ordenação */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
        <h2 className="text-xs font-medium" style={{ color: '#3d2b56' }}>
          Repasses ({repasses.length})
        </h2>

        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <select
              value={sortField}
              onChange={handleSortChange}
              className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
              style={{ 
                borderColor: '#C3B4DE',
                backgroundColor: '#FFFFFF',
                color: '#121212',
                minWidth: '140px'
              }}
            >
              <option value="dataPagamento">Data Pagamento</option>
              <option value="cliente">Cliente</option>
              <option value="numeroDocumento">Proposta</option>
              <option value="repassePago">Valor Pago</option>
              <option value="valorComissao">Comissão</option>
              <option value="percentualRepasse">% Repasse</option>
            </select>
            <button
              onClick={toggleSortOrder}
              className="px-2 py-1 border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 text-xs"
              style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
              title={sortOrder === 'asc' ? 'Ordem crescente' : 'Ordem decrescente'}
            >
              {sortOrder === 'asc' ? '↑' : '↓'}
            </button>
          </div>

          <input
            type="text"
            placeholder="Buscar repasses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', minWidth: '200px' }}
          />

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="px-2 py-1 text-xs font-medium border rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1"
            style={{ borderColor: '#C3B4DE', color: '#7A61B2' }}
          >
            Filtros {showFilters ? '▲' : '▼'}
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="bg-gray-50 p-3 rounded-md">
          <div className="text-sm text-gray-700">Filtros adicionais</div>
        </div>
      )}

      {/* Cards de Repasses */}
      {loading ? (
        <div className="text-center py-8 text-sm text-gray-500">Carregando...</div>
      ) : error ? (
        <div className="text-center py-8 text-sm text-red-500">{error}</div>
      ) : repasses.length === 0 ? (
        <div className="text-center py-8 text-sm text-gray-500">Nenhum repasse encontrado</div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {repasses.map((repasse) => (
            <div
              key={repasse.id}
              className="bg-white rounded-lg shadow-sm border-l-2 p-3 hover:shadow-md transition-shadow"
              style={{ borderLeftColor: '#0B7A3A' }}
            >

              {/* Conteúdo do Card */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                {/* Cliente */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Cliente</div>
                    <div className="text-sm font-semibold text-gray-900 truncate">{repasse.clienteData?.nome || repasse.cliente || 'Cliente não informado'}</div>
                  </div>
                </div>

                {/* Seguradora */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Seguradora</div>
                    <div className="text-sm font-medium text-gray-900 truncate">{repasse.documento?.seguradora?.nome || '-'}</div>
                  </div>
                </div>

                {/* Produto + Ações */}
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-start gap-2 flex-1 min-w-0">
                    <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                    <div>
                      <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Produto</div>
                      <div className="text-sm font-medium text-gray-900 truncate">{repasse.documento?.produto?.nome || '-'}</div>
                    </div>
                  </div>

                  <div className="ml-2 flex-shrink-0 flex items-center gap-1">
                    {/* Editar Apólice */}
                    <button
                      onClick={() => handleOpenApolice(repasse)}
                      className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded transition-colors"
                      title="Editar Apólice"
                      disabled={!repasse.documentoId}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 2v6h6" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11h8M8 15h8M8 7h3" />
                      </svg>
                    </button>

                    {/* Editar Repasse */}
                    {onEdit && (
                      <button
                        onClick={() => onEdit(repasse)}
                        className="p-1 hover:text-green-800 hover:bg-green-50 rounded transition-colors"
                        title="Editar Repasse"
                      >
                        <svg className="w-4 h-4" style={{ color: '#0B7A3A' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                    )}

                    {/* Excluir Repasse */}
                    <button
                      onClick={() => handleDelete(repasse)}
                      className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                      title="Excluir Repasse"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>

                {/* Proposta */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Proposta</div>
                    <div className="text-sm font-medium text-gray-900">{repasse.numeroDocumento || '-'}</div>
                  </div>
                </div>

                {/* Data Pagamento */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Data Pagamento</div>
                    <div className="text-sm font-medium text-gray-900">{repasse.dataPagamento ? formatDate(repasse.dataPagamento) : '-'}</div>
                  </div>
                </div>

                {/* Valor Pago */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Valor Pago</div>
                    <div className="text-sm font-medium text-gray-900">{repasse.repassePago ? formatCurrency(Number(repasse.repassePago)) : '-'}</div>
                  </div>
                </div>

                {/* Comissão */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>Comissão</div>
                    <div className="text-sm font-medium text-gray-900">{repasse.valorComissao ? formatCurrency(Number(repasse.valorComissao)) : '-'}</div>
                  </div>
                </div>

                {/* % Repasse */}
                <div className="flex items-start gap-2">
                  <svg className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: '#7A61B2' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <div className="flex-1 min-w-0">
                    <div className="text-gray-500 uppercase" style={{ fontSize: '11px', letterSpacing: '0.5px' }}>% Repasse</div>
                    <div className="text-sm font-medium text-gray-900">
                      {repasse.percentualRepasse !== undefined && repasse.percentualRepasse !== null 
                        ? `${Number(repasse.percentualRepasse).toFixed(1)}%` 
                        : '-'}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {repasses.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-xs text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            
            <span className="px-2 py-1 text-xs" style={{ color: '#3d2b56' }}>
              Página {currentPage} de {totalPages}
            </span>
            
            <button
              onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-xs border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modal da Apólice */}
      {showApoliceModal && selectedApolice && (
        <ApoliceFormModal
          apolice={selectedApolice}
          isOpen={showApoliceModal}
          isEditing={true}
          onClose={handleCloseApoliceModal}
          onSave={(updatedApolice) => {
            handleCloseApoliceModal();
            loadRepasses();
          }}
          onUpdated={(updatedApolice) => {
            loadRepasses();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={confirmOpen}
        title="Excluir Repasse"
        message="Tem certeza que deseja excluir este repasse?"
        confirmText="Excluir"
        cancelText="Cancelar"
        confirmButtonColor="red"
        onConfirm={handleConfirmDelete}
        onCancel={() => { setConfirmOpen(false); setDeleteTargetItem(null); }}
      />
    </div>
  );
};